import type {
    ModifyPlotAreaProps,
    SeriesInitialProps,
    SeriesPresetPoint,
} from '@src/slide/domain/ui-model/element-modeles/chart-models/chart-presets/sub-chart-presets/base';
import {
    BaseChartPresetHandler,
} from '@src/slide/domain/ui-model/element-modeles/chart-models/chart-presets/sub-chart-presets/base';
import { PresetChartType } from '@src/slide/domain/ui-model/element-modeles/chart-models/chart-presets/const';
import { IBaseSubChart } from '../../../Interface/chart-v3/sub-chart/base-sub-chart.interface';
import { ChartSeries } from '@src/slide/domain/ui-model/element-modeles/chart-models/sub-chart/series-v2';
import { IPieChart } from '../../../Interface/chart-v3/sub-chart';
import { PieChart } from '@src/slide/domain/ui-model/element-modeles/chart-models/sub-chart/pie-chart';
import { IChartAxis, IChartSpace } from '../../../Interface/chart-v3/chart-properties.interface';
import {
    PieChartSeries,
} from '@src/slide/domain/ui-model/element-modeles/chart-models/sub-chart/series-v2/pie-chart-series';
import { TDColor } from '@src/slide/domain/ui-model/element-modeles/property/color/td-color';
import { STSchemeColorVal } from '@tencent/slide-sdk-pb-model';
import { STSchemeColorValList } from '@src/slide/domain/ui-model/element-modeles/theme-model';
import { getThemeColorValueByIndex } from '../../../property/chart/theme';

export const PIE_CHART_SERIES_CATEGORIES_PRESET = ['第一季度', '第二季度', '第三季度', '第四季度'];
export const PIE_CHART_VALUES_PRESET = [[8.2, 3.2, 1.4, 1.2]];
export const PIE_CHART_SERIES_NAME_PRESET = '销售额';

export class PieChartPresetHandler extends BaseChartPresetHandler {
    get ChartSeries() {
        return PieChartSeries;
    }
    get SubChart() {
        return PieChart;
    }

    protected defaultChartDataPreset = {
        categories: PIE_CHART_SERIES_CATEGORIES_PRESET,
        values: PIE_CHART_VALUES_PRESET,
    };

    public modifySubChartType(
        IBaseSubCharts: IBaseSubChart[],
        type: PresetChartType,
        getChartPresetHandlerByChartBaseType,
        chartSpace: IChartSpace,
    ) {
        const newSubChart = super.modifySubChartType(
            IBaseSubCharts, type,
            getChartPresetHandlerByChartBaseType, chartSpace,
        );

        // @ts-ignore
        newSubChart.axisIds = undefined;

        return newSubChart;
    }

    public modifyPlotArea(): ModifyPlotAreaProps {
        return {
            removeProps: {
                categoryAxes: null,
                valueAxes: null,
                dateAxes: null,
            },
        };
    }

    public generateAxesPreset(subChart: IBaseSubChart): {
        categoryAxes?: IChartAxis[];
        valueAxes?: IChartAxis[];
        dataAxes?: IChartAxis[];
    } {
        return {};
    }

    protected generateSeriesPreset(
        ChartSeriesConstructor: typeof ChartSeries,
        valuesPreset: SeriesInitialProps['valuesPreset'],
        categoriesPreset: SeriesInitialProps['categoriesPreset'],
        seriesNamesPreset: SeriesInitialProps['seriesNamesPreset'],
        showDataLabels?: boolean,
        colorPaletteId = '10',
    ) {
        const baseSeriesPreset = super
            .generateSeriesPreset(
                ChartSeriesConstructor,
                valuesPreset,
                categoriesPreset,
                seriesNamesPreset,
                showDataLabels,
            );

        // 将原本在generatePresetSeriesInitialProps中添加的dataPoint转移到这里修改，这里面向uiModel（之前面向sdk）
        baseSeriesPreset.forEach((ser) => {
            ser.dataList.forEach((point, index) => {
                const color = getThemeColorValueByIndex(index, colorPaletteId, ser.dataList.length);
                point.dataPoint = {
                    props: {
                        style: {
                            fillBackground: {
                                solidFill: {
                                    color,
                                },
                            },
                            border: {
                                width: 3,
                                tdColor: new TDColor({
                                    themeKey: STSchemeColorVal.lt1,
                                }),
                            },
                        },
                    },
                };
            });
        });

        return baseSeriesPreset;
    }

    protected generatePresetSeriesInitialProps(
        seriesIndex: number,
        points: Array<SeriesPresetPoint>,
        seriesName?: string,
        showDataLabels = false,
    ) {
        const baseProps = super.generatePresetSeriesInitialProps(seriesIndex, points, seriesName, showDataLabels);

        return {
            ...baseProps,
            seriesText: {
                value: seriesName ?? PIE_CHART_SERIES_NAME_PRESET,
            },
        };
    }

    protected setBaseProps(subChart: IPieChart) {
        super.setBaseProps(subChart);

        // @ts-ignore 饼状图没有坐标轴
        subChart.axisIds = undefined;
        subChart.varyColors = true;
        subChart.firstSliceAngle = 0;
    }

    protected dropSeriesProps(param: {
        oldSer: ChartSeries;
        serIndex: number;
        oldSubChart: IBaseSubChart;
    }): ChartSeries {
        const ser = super.dropSeriesProps(param);

        const { serIndex, oldSubChart } = param;

        ser.dataList = ser.dataList.map((point, pointIndex) => {
            // @ts-ignore todo 待移除
            const sdkPoint = oldSubChart.sdkData?.data?.series[serIndex].categories[pointIndex];

            const sdkPointColorValue = sdkPoint?.dataPoint?.props?.background?.solidFill?.color?.colorValue;
            // 如果饼图的dataPoint的fill是继承自主题色，就将dataPoint的fill改成原本对应的主题色
            if (
                sdkPointColorValue?.startsWith('accent')
                && point.dataPoint?.props?.style.fillBackground.solidFill.color
            ) {
                point.dataPoint.props.style.fillBackground.solidFill = {
                    color: {
                        colorValue: sdkPointColorValue,
                        colorType: 'scheme',
                    },
                };
            }

            return point;
        });

        return ser;
    }
}
